import Keycloak from "keycloak-js";
import type { KeycloakTokenParsed } from "keycloak-js";

export type AuthUser = {
  id: string;
  email: string | null;
  name: string | null;
  roles: string[];
};

let keycloakClient: Keycloak | null = null;
let keycloakInitialization: Promise<boolean> | null = null;

export function isDevAuthDisabled() {
  if (import.meta.env.PROD) return false;
  return isFalseFlag(import.meta.env.VITE_AUTH_REQUIRED);
}

export function getDevAuthUser(): AuthUser {
  return {
    id: "dev-user",
    email: "dev@lainvision.local",
    name: "Dev User",
    roles: ["datavis_admin"],
  };
}

export function getKeycloakClient() {
  if (isDevAuthDisabled()) return null;

  if (!keycloakClient) {
    keycloakClient = new Keycloak(readKeycloakConfig());
  }

  return keycloakClient;
}

export async function initKeycloak() {
  if (isDevAuthDisabled()) return true;

  const keycloak = getKeycloakClient();
  if (!keycloak) return true;

  if (!keycloakInitialization) {
    keycloakInitialization = keycloak.init({
      onLoad: "check-sso",
      pkceMethod: "S256",
      checkLoginIframe: false,
    });
  }

  return await keycloakInitialization;
}

export async function login() {
  const keycloak = getKeycloakClient();
  if (!keycloak) return;
  await keycloak.login();
}

export async function logout() {
  const keycloak = getKeycloakClient();
  if (!keycloak) return;
  await keycloak.logout({ redirectUri: window.location.origin });
}

export async function getAccessToken() {
  const keycloak = getKeycloakClient();
  if (!keycloak?.authenticated) return null;

  await keycloak.updateToken(30);
  return keycloak.token ?? null;
}

export function getCurrentUser(): AuthUser | null {
  if (isDevAuthDisabled()) {
    return getDevAuthUser();
  }

  const keycloak = getKeycloakClient();
  if (!keycloak?.authenticated) return null;
  return buildUserFromToken(keycloak.tokenParsed);
}

function readKeycloakConfig() {
  const url = import.meta.env.VITE_KEYCLOAK_URL?.trim();
  const realm = import.meta.env.VITE_KEYCLOAK_REALM?.trim();
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID?.trim();

  if (!url || !realm || !clientId) {
    throw new Error("Configuration Keycloak frontend manquante.");
  }

  return { url, realm, clientId };
}

function buildUserFromToken(token: KeycloakTokenParsed | undefined): AuthUser | null {
  if (!token?.sub) return null;

  const email = readStringClaim(token, "email");
  const name = readStringClaim(token, "name") ?? readStringClaim(token, "preferred_username");

  return {
    id: token.sub,
    email,
    name,
    roles: collectRoles(token),
  };
}

function collectRoles(token: KeycloakTokenParsed) {
  const roles = new Set<string>();
  token.realm_access?.roles?.forEach((role) => roles.add(role));

  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID?.trim();
  if (clientId) {
    token.resource_access?.[clientId]?.roles?.forEach((role) => roles.add(role));
  }

  return Array.from(roles).sort();
}

function readStringClaim(token: KeycloakTokenParsed, claim: string) {
  const value = token[claim];
  if (typeof value !== "string") return null;
  if (!value.trim()) return null;
  return value;
}

function isFalseFlag(value: string | undefined) {
  if (!value) return false;
  return ["0", "false", "no", "off"].includes(value.trim().toLowerCase());
}
